/**
 * useOtterIntegration — Otter.ai email forwarding-based ingest
 *
 * Users forward Otter meeting summary emails to our ingest address.
 * Provides org ingest config, unassigned meeting notes, and configuration actions.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { bulkAssignNotes } from '@/lib/bulkNoteActions';

// ============================================================================
// TYPES
// ============================================================================

export interface OtterIngestStatus {
  configured: boolean;
  ingestEmail?: string;
  allowedSenderDomains?: string[];
  enabled?: boolean;
  totalIngested?: number;
  lastIngestedAt?: Date | null;
  recentCount?: number;
}

export interface UnassignedMeetingNote {
  id: string;
  name: string;
  meetingDate: Date;
  attendees: string[];
  classificationConfidence: number;
  classificationReasoning: string;
  senderEmail: string;
  metadata: {
    otterSummary?: string;
    otterActionItems?: Array<{ text: string; assignee?: string; completed: boolean }>;
  };
  createdAt: Date;
}

// ============================================================================
// HOOK
// ============================================================================

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && value !== null && '_seconds' in value) {
    return new Date((value as { _seconds: number })._seconds * 1000);
  }
  return new Date(value as string | number);
}

export function useOtterIntegration() {
  const { user, userProfile } = useAuth();
  const [status, setStatus] = useState<OtterIngestStatus>({ configured: false });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [unassignedNotes, setUnassignedNotes] = useState<UnassignedMeetingNote[]>([]);

  // Fetch ingest status on mount
  useEffect(() => {
    if (!user?.uid) {
      setStatus({ configured: false });
      setLoadingStatus(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const fn = httpsCallable(functions, 'getOtterIngestStatus');
        const result = await fn({});
        const data = result.data as OtterIngestStatus & { lastIngestedAt?: unknown };
        setStatus({
          ...data,
          lastIngestedAt: toDate(data.lastIngestedAt),
        });
      } catch (error) {
        console.error('[useOtterIntegration] Failed to fetch status:', error);
        setStatusError('Failed to load Otter integration status');
        toast.error('Could not load Otter integration status');
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchStatus();
  }, [user?.uid]);

  // Real-time listener for unassigned Otter notes (scoped to org)
  const orgId = userProfile?.organizationId;
  useEffect(() => {
    if (!user?.uid || !orgId) {
      setUnassignedNotes([]);
      return;
    }

    const q = query(
      collection(db, 'documents'),
      where('transcriptSource', '==', 'otter'),
      where('organizationId', '==', orgId),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const notes: UnassignedMeetingNote[] = snap.docs
          .filter((d) => d.data().projectId == null)
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name || 'Untitled Meeting',
              meetingDate: data.meetingDate?.toDate?.() || new Date(),
              attendees: data.attendees || [],
              classificationConfidence: data.classificationConfidence || 0,
              classificationReasoning: data.classificationReasoning || '',
              senderEmail: data.senderEmail || '',
              metadata: {
                otterSummary: data.metadata?.otterSummary,
                otterActionItems: data.metadata?.otterActionItems,
              },
              createdAt: data.createdAt instanceof Date ? data.createdAt : (data.createdAt?.toDate?.() || new Date()),
            };
          })
          .sort((a, b) => b.meetingDate.getTime() - a.meetingDate.getTime());

        setUnassignedNotes(notes);
      },
      (error) => {
        console.error('[useOtterIntegration] Unassigned listener error:', error);
      }
    );

    return () => unsub();
  }, [user?.uid, orgId]);

  const configureIngest = useCallback(async (
    allowedDomains: string[],
    enabled: boolean
  ) => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, 'configureOtterIngest');
      const result = await fn({ allowedSenderDomains: allowedDomains, enabled });
      const data = result.data as { success: boolean; ingestEmail: string; allowedSenderDomains: string[]; enabled: boolean };

      setStatus((prev) => ({
        ...prev,
        configured: true,
        ingestEmail: data.ingestEmail,
        allowedSenderDomains: data.allowedSenderDomains,
        enabled: data.enabled,
      }));

      toast.success('Otter ingest configuration saved');
      return true;
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save configuration');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const assignToProject = useCallback(async (documentId: string, projectId: string) => {
    try {
      await bulkAssignNotes([documentId], projectId);
    } catch (error: any) {
      console.error('[useOtterIntegration] Assign failed:', error);
      toast.error('Failed to assign meeting note. Please try again.');
    }
  }, []);

  const unassignedCount = unassignedNotes.length;

  return {
    status,
    loadingStatus,
    statusError,
    saving,
    unassignedNotes,
    unassignedCount,
    configureIngest,
    assignToProject,
  };
}
